/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import Header from "../../../components/Header";
import DataGridButton from "../../../components/DataGridButton";
import { Box, Grid, MenuItem, Tab, Typography, useTheme } from "@mui/material";
import Select from "../../../components/SearchableSelect";
import { Sync } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { tabbedDataGridPage } from "../../../styles/commonPageStyles";
import DbSoftware from "./DbSoftware";
import { useListAllEngines } from "../../../api/softwareImages";
import { useNavigate } from "react-router-dom";
import { useGetReleases } from "../../../api/releases";
import { APPS } from "../../../utils/constants";
import { useNotification } from "../../../components/Notification";
import { MAIN_BRANCH } from "../../../utils/constants";
import { dataGridButtonTypography } from "../../../styles/DataGridStyles";
import styles from "../styles";
import { tokens } from "../../../theme";
import { toKebabCase } from "../../../utils/helperFunctions";
import Os from "./Os";

const queryParamsForGetReleases = "?loadRepos=false&loadCommits=false";

// The tab chain is stretched to the viewport so the table inside a panel can
// claim whatever height is left below the tab list.
const fillHeight = {
  mainBox: {
    ...tabbedDataGridPage.mainBox,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  tabContainer: {
    ...tabbedDataGridPage.tabContainer,
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    paddingLeft: "4px",
    // `display: flex` above would override the browser's [hidden] rule and
    // make the inactive panel claim flex space — keep it collapsed.
    "&[hidden]": { display: "none" },
  },
  tabContentMainBox: {
    ...tabbedDataGridPage.tabContentMainBox,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
};

const SoftwareImagesView = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const [selectedTab, setSelectedTab] = useState("db-software");
  const [releases, setReleases] = useState([]);
  const { notificationComponent, displayNotification } = useNotification();

  const [releaseBranch, setReleaseBranch] = useState(MAIN_BRANCH);

  const [engine, setEngine] = useState("mysql");
  const navigate = useNavigate();

  const { postData: listAllEngines, response: listAllEnginesResp } =
    useListAllEngines();

  const [reloadToggle, setReloadToggle] = useState(false);
  const { postData: getReleases, response: getReleasesResp } = useGetReleases(
    APPS.TESSELL
  );

  useEffect(() => {
    getReleases(queryParamsForGetReleases);
    listAllEngines();
  }, []);

  useEffect(() => {
    navigate(`/apps/tessell/software-images/${selectedTab}`);
  }, [selectedTab]);

  useEffect(() => {
    if (getReleasesResp?.response?.length) {
      setReleases(
        getReleasesResp.response?.map(
          (releaseJson) => releaseJson?.release_label
        )
      );
    }
  }, [getReleasesResp]);

  const SOFTWARE_IMAGE_TYPES = ["DB Software", "OS"];

  return (
    <Box sx={fillHeight.mainBox}>
      <Box sx={fillHeight.tabContainer}>
        <TabContext value={selectedTab}>
          <Box sx={tabbedDataGridPage.tabListBox}>
            <TabList
              onChange={(e, newValue) => setSelectedTab(newValue)}
              sx={tabbedDataGridPage.tabList}
            >
              {SOFTWARE_IMAGE_TYPES?.map((swiType, i) => (
                <Tab
                  key={i}
                  label={swiType}
                  value={toKebabCase(swiType?.toLowerCase())}
                  icon={<></>}
                  iconPosition="end"
                />
              ))}
            </TabList>
          </Box>
          {SOFTWARE_IMAGE_TYPES?.map((swiType, i) => (
            <TabPanel
              key={i}
              value={toKebabCase(swiType?.toLowerCase())}
              sx={fillHeight.tabPanel}
            >
              <Box sx={fillHeight.tabContentMainBox}>
                {notificationComponent}
                <Grid
                  sx={tabbedDataGridPage.gridContainer}
                  container
                  columns={2}
                >
                  <Grid item xs={1}>
                    <Header
                      title={swiType}
                      subtitle={`View all ${swiType?.toLowerCase()} of ${engine}`}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Box sx={styles.topControlsStack}>
                      <Box>
                        <Typography sx={styles.filterLabel(colors)}>
                          Engine
                        </Typography>
                        <Select
                          id="select-engine"
                          size="small"
                          value={engine}
                          sx={styles.filterSelect}
                          onChange={(e) => setEngine(e.target.value)}
                        >
                          {listAllEnginesResp?.dbEngines?.map((engine, i) => (
                            <MenuItem value={engine} key={i}>
                              {engine}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                      <Box>
                        <Typography sx={styles.filterLabel(colors)}>
                          Release
                        </Typography>
                        <Select
                          id="select-release-branch"
                          size="small"
                          value={releaseBranch}
                          sx={styles.filterSelect}
                          onChange={(e) => setReleaseBranch(e.target.value)}
                        >
                          {[MAIN_BRANCH, ...(releases || [])].map(
                            (release, i) => (
                              <MenuItem value={release} key={i}>
                                {release}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </Box>
                      <DataGridButton
                        sx={styles.reloadButton}
                        onClick={() => setReloadToggle((prev) => !prev)}
                      >
                        <Sync sx={styles.reloadIcon} />
                        <Typography sx={dataGridButtonTypography(colors, 500)}>
                          Reload
                        </Typography>
                      </DataGridButton>
                    </Box>
                  </Grid>
                </Grid>
                {swiType === "DB Software" ? (
                  <DbSoftware
                    engine={engine}
                    releaseBranch={releaseBranch}
                    displayNotification={displayNotification}
                    reloadToggle={reloadToggle}
                  />
                ) : (
                  <Os
                    engine={engine}
                    releaseBranch={releaseBranch}
                    displayNotification={displayNotification}
                    reloadToggle={reloadToggle}
                  />
                )}
              </Box>
            </TabPanel>
          ))}
        </TabContext>
      </Box>
    </Box>
  );
};

export default SoftwareImagesView;
